import {ServerErrorFormBanner} from '@github-ui/custom-properties-core/components/ServerErrorFormBanner'
import {useDiscardConfirmation} from '@github-ui/custom-properties-core/hooks/use-discard-confirmation'
import {
  DEFAULT_FIELD_ERRORS_ORDER,
  DEFAULT_INCLUDED_FORM_VALIDATION_FIELD_ERRORS,
  getFirstInvalidFieldKey,
} from '@github-ui/custom-properties-core/hooks/use-property-definition-form'
import {useRefs} from '@github-ui/custom-properties-core/hooks/use-refs'
import {VerifiedHTMLText} from '@github-ui/safe-html/VerifiedHTML'
import {sanitize} from '@github-ui/sanitize'
import sudo from '@github-ui/sudo'
import type {DialogButtonProps} from '@primer/react'
import {Dialog} from '@primer/react/experimental'
import {type ComponentProps, useEffect, useRef, useState} from 'react'

import styles from './custom-properties-sidebar-form.module.css'
import {
  type RepoPropertyDefinitionWithValueSchema,
  useDefinitionFormWithValue,
} from './hooks/use-definition-form-with-value'
import {MultiStepForm} from './components/MultiStepForm'
import type {Repository} from './types'
import {useMutation} from '@github-ui/react-query'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {getCreateDefinitionAndSetValuePath} from './paths'
import {useMultiStepForm} from './hooks/use-multi-step-form'
import type {PropertyDefinition, PropertyValue} from '@github-ui/custom-properties-types'

const TITLES = ['New custom property', 'Set values']

interface Props {
  anchorRef: ComponentProps<typeof Dialog>['returnFocusRef']
  repositories: Repository[]
  sourceName: string
  onClose: () => void
  onSuccess: (propertyName: string, value: PropertyValue) => void
}

const BLOCKING_ERRORS: Array<keyof RepoPropertyDefinitionWithValueSchema> = [
  ...DEFAULT_INCLUDED_FORM_VALIDATION_FIELD_ERRORS,
  'appliedValue',
]
const ERRORS_ORDER: Array<keyof RepoPropertyDefinitionWithValueSchema> = [...DEFAULT_FIELD_ERRORS_ORDER, 'appliedValue']
const FIELDS_STEP_MAP: Record<keyof RepoPropertyDefinitionWithValueSchema, number> = {
  propertyName: 0,
  description: 0,
  valueType: 0,
  regexPattern: 0,
  allowedValues: 0,
  regexEnabled: 0,
  newAllowedValue: 0,
  defaultValue: 0,
  requireExplicitValues: 0,
  valuesEditableBy: 0,
  required: 0,
  appliedValue: 1,
}

const FIELD_KEYS = Object.keys(FIELDS_STEP_MAP) as Array<keyof RepoPropertyDefinitionWithValueSchema>

export function CustomPropertiesSidebarForm({anchorRef, onClose, onSuccess, repositories, sourceName}: Props) {
  const [formError, setFormError] = useState<string>()
  const [saving, setSaving] = useState(false)
  const {nextStep, prevStep, title, subtitle, step} = useMultiStepForm(TITLES)

  const serverErrorBannerRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (formError) serverErrorBannerRef.current?.focus()
  }, [formError])

  const {formProps, propertiesFetchError} = useDefinitionFormWithValue({sourceName})

  const refProps = useRefs<RepoPropertyDefinitionWithValueSchema>()

  const [initialFieldValues] = useState(() =>
    Object.fromEntries(FIELD_KEYS.map(key => [key, JSON.stringify(formProps.getFieldValue(key))])),
  )
  const isDirty = FIELD_KEYS.some(key => JSON.stringify(formProps.getFieldValue(key)) !== initialFieldValues[key])

  const {discardWithConfirmation: requestClose} = useDiscardConfirmation({
    hasUnsavedChanges: isDirty,
    onConfirm: onClose,
  })

  const validateStep = async (currentStep: number): Promise<boolean> => {
    setFormError(undefined)

    const includedFields = BLOCKING_ERRORS.filter(field => FIELDS_STEP_MAP[field] === currentStep)
    const errors = await formProps.validateForm(includedFields)

    if (errors.formErrors.length) {
      setFormError(errors.formErrors[0] || '')
      return false
    }

    const key = getFirstInvalidFieldKey(errors, ERRORS_ORDER)
    if (key) {
      refProps.getRef(key)?.focus()
      return false
    }

    return true
  }

  const onNext = async () => {
    if (propertiesFetchError) return
    if (await validateStep(step)) {
      nextStep()
    }
  }

  const formMutation = useMutation({
    mutationFn: (body: SaveRequestPayload) => saveDefinition(getCreateDefinitionAndSetValuePath({sourceName}), body),
    onSuccess: data => onSuccess(data.propertyName, data.value),
    onError: error => setFormError(error.message),
    onSettled: () => setSaving(false),
  })

  const onSubmit = async () => {
    if (propertiesFetchError) return

    setFormError(undefined)
    if (!(await validateStep(step))) return

    // Unmount the dialog while the sudo prompt is open so its focus trap releases and the
    // sudo prompt can receive focus.
    setSaving(true)

    if (!(await sudo({target: getCreateDefinitionAndSetValuePath({sourceName}), method: 'POST'}))) {
      setFormError('Unauthorized')
      return setSaving(false)
    }

    const valueType = formProps.getFieldValue('valueType')
    const isSelect = valueType === 'single_select' || valueType === 'multi_select'
    const allowedValues = isSelect ? formProps.getFieldValue('allowedValues') : null
    const regex = (formProps.getFieldValue('regexEnabled') && formProps.getFieldValue('regexPattern')) || null
    const targetIds = repositories.map(r => r.id)

    const body = {
      valueType,
      allowedValues,
      regex,
      targetIds,
      propertyName: formProps.getFieldValue('propertyName'),
      required: formProps.getFieldValue('required'),
      defaultValue: formProps.getFieldValue('required') ? formProps.getFieldValue('defaultValue') : null,
      description: formProps.getFieldValue('description').trim() || null,
      valuesEditableBy: formProps.getFieldValue('valuesEditableBy'),
      requireExplicitValues: formProps.getFieldValue('required') && formProps.getFieldValue('requireExplicitValues'),
      value: formProps.getFieldValue('appliedValue'),
    }

    formMutation.mutate(body)
  }

  const footerButtons: DialogButtonProps[][] = [
    [
      {buttonType: 'default', content: 'Cancel', onClick: requestClose},
      {buttonType: 'primary', content: 'Next', onClick: onNext},
    ],
    [
      {buttonType: 'default', content: 'Back', onClick: prevStep},
      {buttonType: 'primary', content: 'Save and apply', onClick: onSubmit},
    ],
  ]

  const displayedFormError = formError || (propertiesFetchError ? 'Failed to fetch properties' : undefined)

  // Escape the dialog's focus trap while the sudo prompt is open
  if (saving) return null

  return (
    <Dialog
      title={title}
      subtitle={subtitle}
      onClose={requestClose}
      position="right"
      returnFocusRef={anchorRef}
      footerButtons={footerButtons[step]}
      renderBody={({children}) => <Dialog.Body className={styles.dialogBody}>{children}</Dialog.Body>}
    >
      {displayedFormError && (
        <div className="mb-3">
          <ServerErrorFormBanner ref={serverErrorBannerRef}>
            <VerifiedHTMLText html={sanitize(displayedFormError)} />
          </ServerErrorFormBanner>
        </div>
      )}
      <MultiStepForm sourceName={sourceName} step={step} repositories={repositories} {...formProps} {...refProps} />
    </Dialog>
  )
}

interface SaveRequestPayload extends Partial<PropertyDefinition> {
  propertyName: string
  value: PropertyValue
}

async function saveDefinition(path: string, body: SaveRequestPayload) {
  const response = await reactFetchJSON(path, {
    method: 'POST',
    body,
  })

  if (response.ok) {
    return body
  }

  let errorMessage = 'Something went wrong.'
  try {
    const data = (await response.json()) as {error?: string}
    errorMessage = data?.error || errorMessage
  } catch {
    // Fallback to generic error
  }

  // useMutation takes care of the error handling.
  // By re-throwing the error we pipe the error message into the onError handler
  throw new Error(errorMessage)
}
